/*
 * Copyright 2021, Offchain Labs, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/* eslint-env node */
'use strict';
Object.defineProperty(exports, "__esModule", { value: true });
exports.L1Bridge = void 0;
const abi_1 = require("@ethersproject/abi");
const bignumber_1 = require("@ethersproject/bignumber");
const constants_1 = require("@ethersproject/constants");
const L1GatewayRouter__factory_1 = require("./abi/factories/L1GatewayRouter__factory");
const L1ERC20Gateway__factory_1 = require("./abi/factories/L1ERC20Gateway__factory");
const Inbox__factory_1 = require("./abi/factories/Inbox__factory");
const ERC20__factory_1 = require("./abi/factories/ERC20__factory");
const Multicall2__factory_1 = require("./abi/factories/Multicall2__factory");
const bridge_helpers_1 = require("./bridge_helpers");
const MIN_APPROVAL = constants_1.MaxUint256;
/**
 * L1 side only of {@link Bridge}
 */
class L1Bridge {
    constructor(network, l1Signer) {
        this.l1Signer = l1Signer;
        this.network = network;
        const l1Provider = l1Signer.provider;
        if (l1Provider === undefined) {
            throw new Error('Signer must be connected to an Ethereum provider');
        }
        this.l1Provider = l1Provider;
        this.l1GatewayRouter = L1GatewayRouter__factory_1.L1GatewayRouter__factory.connect(network.tokenBridge.l1GatewayRouter, l1Signer);
    }
    async setSigner(newSigner) {
        var _a;
        const newL1Provider = newSigner.provider;
        if (newL1Provider === undefined) {
            throw new Error('Signer must be connected to an Ethereum provider');
        }
        // check chainId to ensure its still in the same network.
        const [prevNetwork, newNetwork] = await Promise.all([
            this.l1Provider.getNetwork(),
            newL1Provider.getNetwork(),
        ]);
        if (prevNetwork.chainId !== newNetwork.chainId)
            throw new Error('Error. New signer in L1 is a different network.');
        this.l1Provider = newL1Provider;
        this.l1Signer = newSigner;
        // we need to update the cache
        // TODO: remove this cache. can we memoize based on the signer? useCallback style
        this.walletAddressCache = await this.l1Signer.getAddress();
        // TODO: is it worth keeping contracts instantiated?
        this.inboxCached = (_a = this.inboxCached) === null || _a === void 0 ? void 0 : _a.connect(newSigner);
        this.l1GatewayRouter = this.l1GatewayRouter.connect(newSigner);
    }
    getERC20L2Address(erc20L1Address) {
        return this.l1GatewayRouter.functions
            .calculateL2TokenAddress(erc20L1Address)
            .then(([res]) => res);
    }
    async contractExists(contractAddress) {
        const contractCode = await this.l1Provider.getCode(contractAddress);
        return !(contractCode.length > 2);
    }
    async getL1TokenData(erc20L1Address) {
        const walletAddress = await this.getWalletAddress();
        const gatewayAddress = await this.getGatewayAddress(erc20L1Address);
        const ethERC20TokenContract = await ERC20__factory_1.ERC20__factory.connect(erc20L1Address, this.l1Signer);
        // If this will throw if not a contract / ERC20, which is what we *want*.
        const iface = ERC20__factory_1.ERC20__factory.createInterface();
        const functionCalls = [
            {
                target: erc20L1Address,
                funcFragment: iface.functions['balanceOf(address)'],
                values: [walletAddress],
            },
            {
                target: erc20L1Address,
                funcFragment: iface.functions['allowance(address,address)'],
                values: [walletAddress, gatewayAddress],
            },
            {
                target: erc20L1Address,
                funcFragment: iface.functions['symbol()'],
            },
            {
                target: erc20L1Address,
                funcFragment: iface.functions['decimals()'],
            },
            {
                target: erc20L1Address,
                funcFragment: iface.functions['name()'],
            },
        ];
        const [balanceResult, allowanceResult, symbolResult, decimalsResult, nameResult,] = await this.getMulticallAggregate(functionCalls);
        const isString = (x) => typeof x === 'string';
        const balance = (() => {
            if (!balanceResult)
                throw new Error('No balance method available');
            if (isString(balanceResult))
                throw new Error('Not able to decode balance');
            return balanceResult[0];
        })();
        const allowance = (() => {
            if (!allowanceResult)
                throw new Error('No allowance method available');
            if (isString(allowanceResult))
                throw new Error('Not able to decode allowance');
            return allowanceResult[0];
        })();
        const symbol = (() => {
            if (!symbolResult)
                return (0, bridge_helpers_1.addressToSymbol)(erc20L1Address);
            if (isString(symbolResult))
                return symbolResult;
            return symbolResult[0];
        })();
        const decimals = (() => {
            if (!decimalsResult)
                return 18;
            if (isString(decimalsResult))
                throw new Error('Not able to decode decimals');
            return decimalsResult[0];
        })();
        const name = (() => {
            if (!nameResult)
                return symbol + '_Token';
            if (isString(nameResult))
                return nameResult;
            return nameResult[0];
        })();
        const allowanceLimit = bignumber_1.BigNumber.from('0xffffffffffffffffffffffff'); /** for ERC20s that cap approve at 96 bits  */
        const allowed = allowance.gte(allowanceLimit.div(2));
        return {
            contract: ethERC20TokenContract,
            balance,
            allowed,
            symbol,
            decimals,
            name,
        };
    }
    async estimateGasDepositEth(value, maxSubmissionPrice, overrides = {}) {
        if (overrides.value)
            console.warn('You are overriding value argument in an eth deposit. Be careful.');
        const inbox = await this.getInbox();
        return inbox.estimateGas.depositEth(maxSubmissionPrice, Object.assign({ value }, overrides));
    }
    async depositETH(value, maxSubmissionPrice, overrides = {}) {
        if (overrides.value)
            console.warn('You are overriding value argument in an eth deposit. Be careful.');
        const inbox = await this.getInbox();
        return inbox.functions.depositEth(maxSubmissionPrice, Object.assign({ value }, overrides));
    }
    async getGatewayAddress(erc20L1Address) {
        return (await this.l1GatewayRouter.functions.getGateway(erc20L1Address))
            .gateway;
    }
    async getDefaultL1Gateway() {
        const defaultGatewayAddress = await this.l1GatewayRouter.defaultGateway();
        if (defaultGatewayAddress === constants_1.AddressZero) {
            console.log('No default network assigned in contract, using standard l1ERC20Gateway:');
            return L1ERC20Gateway__factory_1.L1ERC20Gateway__factory.connect(this.network.tokenBridge.l1ERC20Gateway, this.l1Provider);
        }
        return L1ERC20Gateway__factory_1.L1ERC20Gateway__factory.connect(defaultGatewayAddress, this.l1Provider);
    }
    async approveToken(erc20L1Address, amount, overrides = {}) {
        // you approve tokens to the gateway that the router will use
        const gatewayAddress = await this.getGatewayAddress(erc20L1Address);
        const contract = await ERC20__factory_1.ERC20__factory.connect(erc20L1Address, this.l1Signer);
        return contract.functions.approve(gatewayAddress, amount || MIN_APPROVAL, overrides);
    }
    async estimateGasDeposit(depositParams, overrides = {}) {
        const data = abi_1.defaultAbiCoder.encode(['uint256', 'bytes'], [depositParams.maxSubmissionCost, '0x']);
        return this.l1GatewayRouter.estimateGas.outboundTransfer(depositParams.erc20L1Address, depositParams.destinationAddress, depositParams.amount, depositParams.maxGas, depositParams.gasPriceBid, data, Object.assign(Object.assign({}, overrides), { value: depositParams.l1CallValue }));
    }
    async deposit(depositParams, overrides = {}) {
        const data = abi_1.defaultAbiCoder.encode(['uint256', 'bytes'], [depositParams.maxSubmissionCost, '0x']);
        if (overrides.value)
            throw new Error('L1 call value should be set through l1CallValue param');
        if (depositParams.l1CallValue.eq(0))
            throw new Error('L1 call value should not be zero');
        if (depositParams.maxSubmissionCost.eq(0))
            throw new Error('Max submission cost should not be zero');
        return this.l1GatewayRouter.functions.outboundTransfer(depositParams.erc20L1Address, depositParams.destinationAddress, depositParams.amount, depositParams.maxGas, depositParams.gasPriceBid, data, Object.assign(Object.assign({}, overrides), { value: depositParams.l1CallValue }));
    }
    async getWalletAddress() {
        if (this.walletAddressCache) {
            return this.walletAddressCache;
        }
        this.walletAddressCache = await this.l1Signer.getAddress();
        return this.walletAddressCache;
    }
    async getInbox() {
        if (this.inboxCached) {
            return this.inboxCached;
        }
        const gateway = await this.getDefaultL1Gateway();
        const inboxAddress = await gateway.inbox();
        this.inboxCached = Inbox__factory_1.Inbox__factory.connect(inboxAddress, this.l1Signer);
        return this.inboxCached;
    }
    getL1EthBalance() {
        return this.l1Signer.getBalance();
    }
    async getMulticallAggregate(functionCalls) {
        const multicall = Multicall2__factory_1.Multicall2__factory.connect(this.network.tokenBridge.l1MultiCall, this.l1Provider);
        return bridge_helpers_1.BridgeHelper.getMulticallTryAggregate(functionCalls, multicall);
    }
    async tokenIsDisabled(l1TokenAddress) {
        return ((await this.l1GatewayRouter.l1TokenToGateway(l1TokenAddress)) ===
            '0x0000000000000000000000000000000000000001');
    }
}
exports.L1Bridge = L1Bridge;
